import { useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { ip_back } from "../config";
import { userHasParentRole } from "../utils/homeRole";

/**
 * True when the account still needs a confirm-email step.
 * Skip for already-verified users, demo walk-ins, and parents who joined
 * via club invitation / parent onboarding (email ownership already proven).
 */
export function needsEmailVerificationBanner(user) {
  if (!user?.user_id) return false;
  if (user.emailVerification === true) return false;
  if (user.is_demo_walkin === true) return false;
  if (userHasParentRole(user) || Number(user.type_user_id) === 5) return false;
  // Club invite flow already validates the mailbox via the invite link
  if (
    user.invitation_sent_at ||
    user.invited_via_email === true ||
    String(user.invitation_type || "").includes("invite")
  ) {
    return false;
  }
  return true;
}

/**
 * Shown when the player has an account but has not confirmed email yet.
 * Profile/home are allowed; training participation is blocked until verified.
 */
export function EmailVerificationBanner({ user, onVerified }) {
  const { t } = useTranslation();
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  if (!needsEmailVerificationBanner(user)) {
    return null;
  }

  const resend = async () => {
    setSending(true);
    setError("");
    setMessage("");
    try {
      const token = user.user_token || localStorage.getItem("token");
      const res = await axios.post(
        `${ip_back}api/user/resendEmailVerification`,
        {},
        {
          headers: {
            Authorization: token,
            "user-id": user.user_id,
            "Content-Type": "application/json",
          },
        }
      );
      if (res.data?.status === "success") {
        if (res.data?.data?.emailVerification === true) {
          try {
            const raw = localStorage.getItem("user");
            if (raw) {
              const u = JSON.parse(raw);
              u.emailVerification = true;
              localStorage.setItem("user", JSON.stringify(u));
            }
          } catch {
            /* ignore */
          }
          onVerified?.();
          setMessage(
            t("emailAlreadyVerified") || "Your email is already verified."
          );
        } else {
          setMessage(
            t("emailVerificationResent") ||
              "Confirmation email sent. Check your inbox (and spam)."
          );
        }
      } else {
        setError(res.data?.message || "Failed to resend email");
      }
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err.message ||
          "Failed to resend confirmation email"
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="email-verification-banner"
      style={{
        margin: "12px 16px",
        padding: "14px 16px",
        borderRadius: 12,
        background: "rgba(255, 159, 10, 0.12)",
        border: "1px solid rgba(255, 159, 10, 0.45)",
        color: "inherit",
      }}
      role="status"
    >
      <div style={{ fontWeight: 600, marginBottom: 6 }}>
        {t("emailVerificationRequiredTitle") || "Confirm your email"}
      </div>
      <p style={{ margin: "0 0 10px", fontSize: 14, opacity: 0.9 }}>
        {t("emailVerificationRequiredBody") ||
          "Your profile is ready, but you must confirm your email before joining a training session. Check your inbox for the link we sent."}
      </p>
      <button
        type="button"
        onClick={resend}
        disabled={sending}
        style={{
          border: "none",
          borderRadius: 8,
          padding: "8px 14px",
          fontWeight: 600,
          cursor: sending ? "wait" : "pointer",
          background: "var(--brand-primary, #0877BC)",
          color: "#fff",
        }}
      >
        {sending
          ? t("sendingEmail") || "Sending…"
          : t("resendConfirmationEmail") || "Resend confirmation email"}
      </button>
      {message ? (
        <p style={{ margin: "8px 0 0", fontSize: 13, color: "#1a7f37" }}>
          {message}
        </p>
      ) : null}
      {error ? (
        <p style={{ margin: "8px 0 0", fontSize: 13, color: "#c62828" }}>
          {error}
        </p>
      ) : null}
    </div>
  );
}

export default EmailVerificationBanner;
